import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const TermsOfService: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Terms of Service — FitMind AI';
  }, []);

  const lastUpdated = 'April 8, 2025';

  return (
    <div className="legal-page">
      <header className="legal-header">
        <Link to="/" className="legal-logo">
          <img src="/logo.png" alt="FitMind AI" />
        </Link>
      </header>

      <main className="legal-main container">
        <div className="legal-content">
          <div className="legal-title-block">
            <span className="legal-tag">Legal</span>
            <h1>Terms of Service</h1>
            <p className="legal-meta">Last updated: {lastUpdated}</p>
          </div>

          <p className="legal-intro">
            Please read these Terms of Service ("Terms") carefully before using the FitMind AI
            mobile application. By downloading, installing or using the app, you agree to be bound
            by these Terms. If you do not agree, do not use FitMind AI.
          </p>

          <Section title="1. About FitMind AI">
            <p>
              FitMind AI is an AI-powered fitness coaching application that provides personalised
              training plans, nutrition guidance and an AI coaching assistant. The app is available
              on Android via Google Play. For questions, contact us at{' '}
              <a href="mailto:fitmindai@gmail.com">fitmindai@gmail.com</a>.
            </p>
          </Section>

          <Section title="2. Eligibility">
            <p>
              You must be at least 13 years old to use FitMind AI. By using the app, you confirm
              that you meet this age requirement. If you are under 18, you confirm that you have
              obtained parental or guardian consent.
            </p>
          </Section>

          <Section title="3. Your Account">
            <ul>
              <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
              <li>You are responsible for all activity that occurs under your account.</li>
              <li>You must provide accurate and complete information during registration and onboarding.</li>
              <li>You may not share your account with others or create accounts for third parties.</li>
              <li>We reserve the right to suspend or terminate accounts that violate these Terms.</li>
            </ul>
          </Section>

          <Section title="4. Subscription & Payments">
            <SubSection title="4.1 Free Plan">
              <p>
                FitMind AI offers a free tier with limited features, including one AI-generated
                training block, basic nutrition guidelines, session tracking and limited AI Coach access.
                No payment is required for the free plan.
              </p>
            </SubSection>
            <SubSection title="4.2 Pro Subscription">
              <p>
                The Pro plan is a monthly subscription that unlocks all features of FitMind AI.
                Subscription pricing is displayed in the app and on our website prior to purchase.
              </p>
              <ul>
                <li>Billing is monthly, charged through your Google Play account.</li>
                <li>Subscriptions automatically renew unless cancelled at least 24 hours before the renewal date.</li>
                <li>You can manage and cancel your subscription at any time through Google Play settings.</li>
              </ul>
            </SubSection>
            <SubSection title="4.3 Free Trial">
              <p>
                We may offer a free trial period for the Pro plan. If you do not cancel before the
                trial ends, you will be charged the regular subscription price. Only one free trial
                per Google account is permitted.
              </p>
            </SubSection>
            <SubSection title="4.4 Refunds">
              <p>
                All payments are processed by Google Play. Refund requests are governed by Google Play's
                refund policy. We do not issue refunds directly. If you believe you are entitled to a
                refund, contact Google Play support.
              </p>
            </SubSection>
          </Section>

          <Section title="5. AI-Generated Content & Health Disclaimer">
            <div className="legal-warning-box">
              <strong>⚠️ Important Health Notice</strong>
              <p>
                FitMind AI provides general fitness and nutrition information generated by artificial
                intelligence. The content provided — including training plans, nutrition guides, weight
                suggestions and AI Coach responses — is for informational and motivational purposes only.
                It is not medical advice, and it does not replace professional medical, nutritional or
                personal training advice.
              </p>
            </div>
            <ul>
              <li>Always consult a qualified healthcare professional before starting a new fitness programme,
                especially if you have any medical conditions, injuries or health concerns.</li>
              <li>FitMind AI is not a licensed medical provider, dietitian, or certified personal trainer.</li>
              <li>AI suggestions (weights, intensities, nutrition) are estimates based on data you provide.
                You are solely responsible for using your own judgement when following any recommendation.</li>
              <li>If you feel pain, discomfort or illness during exercise, stop immediately and seek medical advice.</li>
            </ul>
          </Section>

          <Section title="6. Acceptable Use">
            <p>You agree not to:</p>
            <ul>
              <li>Use the app for any unlawful purpose or in violation of any applicable laws</li>
              <li>Attempt to reverse engineer, decompile or tamper with the app</li>
              <li>Access or attempt to access other users' data</li>
              <li>Upload content that is offensive, illegal, or violates third-party rights</li>
              <li>Use automated tools or scripts to interact with the app</li>
              <li>Misuse or abuse the AI Coach in a way that could harm yourself or others</li>
              <li>Share or distribute your subscription access with others</li>
            </ul>
          </Section>

          <Section title="7. User-Generated Content">
            <p>
              You may upload progress photos and other content to FitMind AI. By doing so, you grant us
              a limited, non-exclusive licence to store and display this content solely for the purpose
              of providing the app's features to you. We do not use your photos for any other purpose.
            </p>
            <p>
              You retain full ownership of any content you upload. You are responsible for ensuring
              that uploaded content does not violate any third-party rights or applicable laws.
            </p>
          </Section>

          <Section title="8. Intellectual Property">
            <p>
              All content, features, design and technology within FitMind AI — including but not limited
              to the AI training system, UI design, branding and generated plans — are owned by FitMind AI
              and protected by applicable intellectual property laws.
            </p>
            <p>
              You are granted a limited, non-transferable, revocable licence to use the app for personal,
              non-commercial purposes. You may not reproduce, distribute or create derivative works
              from any part of FitMind AI without our prior written consent.
            </p>
          </Section>

          <Section title="9. Availability & Updates">
            <p>
              We strive to keep FitMind AI available at all times, but we do not guarantee uninterrupted
              access. The app may be temporarily unavailable due to maintenance, updates or issues beyond
              our control.
            </p>
            <p>
              We reserve the right to modify, update or discontinue features of the app at any time.
              Where possible, we will give notice of significant changes.
            </p>
          </Section>

          <Section title="10. Limitation of Liability">
            <p>
              To the maximum extent permitted by applicable law, FitMind AI and its creators shall not
              be liable for:
            </p>
            <ul>
              <li>Any injury, health issue or physical harm arising from following the app's recommendations</li>
              <li>Loss of data, profits or business opportunities</li>
              <li>Any indirect, incidental or consequential damages arising from your use of the app</li>
              <li>Errors or inaccuracies in AI-generated content</li>
            </ul>
            <p>
              Your use of FitMind AI is entirely at your own risk.
            </p>
          </Section>

          <Section title="11. Termination">
            <p>
              You may stop using FitMind AI and delete your account at any time. We may suspend or
              terminate your access if you violate these Terms, without prior notice.
            </p>
            <p>
              Upon termination, your right to use the app ceases immediately. Sections relating to
              intellectual property, disclaimers and limitation of liability survive termination.
            </p>
          </Section>

          <Section title="12. Changes to These Terms">
            <p>
              We may update these Terms from time to time. We will notify you of significant changes
              via the app or email. Continued use of FitMind AI after changes are posted constitutes
              your acceptance of the updated Terms.
            </p>
          </Section>

          <Section title="13. Governing Law">
            <p>
              These Terms are governed by and construed in accordance with applicable law. Any disputes
              arising from these Terms or your use of FitMind AI shall be resolved through good-faith
              negotiation. If unresolved, disputes shall be subject to the jurisdiction of the courts
              in the developer's country of residence.
            </p>
          </Section>

          <Section title="14. Contact Us">
            <p>If you have any questions about these Terms, please contact us:</p>
            <div className="legal-contact-box">
              <div><strong>FitMind AI</strong></div>
              <div>Email: <a href="mailto:fitmindai@gmail.com">fitmindai@gmail.com</a></div>
            </div>
          </Section>
        </div>
      </main>

      <footer className="legal-footer">
        <div className="container">
          <Link to="/">← Back to FitMind AI</Link>
          <span>·</span>
          <Link to="/privacy">Privacy Policy</Link>
          <span>·</span>
          <span>© {new Date().getFullYear()} FitMind AI</span>
        </div>
      </footer>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="legal-section">
    <h2>{title}</h2>
    {children}
  </section>
);

const SubSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="legal-subsection">
    <h3>{title}</h3>
    {children}
  </div>
);

export default TermsOfService;
